import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import { useSubcategories } from "../../../hooks/useSubcategories";
import MenuVariantForm from "./MenuVariantForm";
import ImageUploadPreview from "../ImageUploadPreview";
import { showToast } from "../../../helper/generalHelper";
import { FaPlus, FaSync } from "react-icons/fa";
import CategoryDropdown from "../../common/CategoryDropdown";

// const token = localStorage.getItem("token");
const supportedLanguages = ["en", "hi"];

const AdvancedMenuForm = ({ inventory }) => {
  const navigate = useNavigate();
  const { subcategories, refetchSubcategories } = useSubcategories();

  const [categoryId, setCategoryId] = useState("");
  const [translations, setTranslations] = useState(
    supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { title: "", shortDescription: "", description: "" };
      return acc;
    }, {})
  );
  const [activeLang, setActiveLang] = useState("en");
  const [imagesObj, setImagesObj] = useState([]);
  const [images, setImages] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [variantForms, setVariantForms] = useState([Date.now()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryActionLoading, setCategoryActionLoading] = useState(false);
  const [newCategory, setNewCategory] = useState({
    translations: supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { name: "", description: "" };
      return acc;
    }, {}),
    activeLang: "en"
  });
  const [isRefreshingCategories, setIsRefreshingCategories] = useState(false);

  const variantRefs = useRef([]);

  const handleCreateCategory = async () => {
    if (!newCategory.translations.en.name.trim()) {
      toast.error("Category name in English is required");
      return;
    }

    try {
      setCategoryActionLoading(true);
      const categoryData = [
        {
          en: newCategory.translations.en,
          hi: newCategory.translations.hi
        }
      ];
      const res = await api.post("restaurant/subcategory/request", categoryData);
      if (res) {
        toast.success("Category added successfully");
        setShowCategoryModal(false);
        setNewCategory({
          translations: supportedLanguages.reduce((acc, lang) => {
            acc[lang] = { name: "", description: "" };
            return acc;
          }, {}),
          activeLang: "en"
        });
        if (refetchSubcategories) refetchSubcategories();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to create category");
    } finally {
      setCategoryActionLoading(false);
    }
  };

  const addVariantForm = () => {
    setVariantForms((prev) => [...prev, Date.now() + Math.random()]);
  };

  const removeVariantForm = (indexToRemove) => {
    setVariantForms((prev) => prev.filter((_, i) => i !== indexToRemove));
    variantRefs.current.splice(indexToRemove, 1);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    const variants = variantRefs.current
      .map((ref) => ref?.getSanitizedData())
      .filter(Boolean);

    const childUploads = variantRefs.current.flatMap(
      (ref) => ref?.getUploads?.() || []
    );

    if (!categoryId) {
      toast.error("Please select a Category.");
      return;
    }

    if (!translations.en.title.trim()) {
      toast.error("Please enter the Item Name.");
      return;
    }

    if (variants.length === 0) {
      toast.error("Please add at least one variant.");
      return;
    }
    
    setIsSubmitting(true);

    // Generate random SKU for parent — simple example using timestamp
    const generateSKU = () =>
      `SKU-${Math.floor(100000 + Math.random() * 900000)}`;

    // Use first variant for fields like price, quantity etc.
    const firstVariant = variants[0];

    const parentMenu = {
      categoryId: Number(categoryId),
      sku: generateSKU(),
      mrp: firstVariant.mrp,
      price: firstVariant.price,
      quantity: firstVariant.quantity,
      hasTax: firstVariant.hasTax,
      tax: firstVariant.hasTax ? (firstVariant.tax || 0) : 0,
      inventory_manage: firstVariant.inventory_manage,
      ingredients: firstVariant.ingredients,
      translations,
      images,
      has_variation: 1,
    };

    const menus = [parentMenu, ...variants];

    const payload = {
      menus,
      // uploads: [...uploads, ...childUploads],
      uploads: [],
    };

    try {
      const res = await api.post("restaurant/restaurant/menu/new", payload);

      showToast(res);
      if (res.success) {
        const isStaff = window.location.pathname.includes("/restaurant-staff");
        navigate(isStaff ? "/restaurant-staff/employee/menu/items" : "/restaurant/menus/all");
      }
    } catch (err) {
      console.error(err);
      toast.error("Submission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ===== Parent Section: Category + Translations ===== */}
      <div className="p-6 border rounded bg-white shadow-md">
        {/* Translations */}
        <div className="mb-4">
          <div className="flex gap-2 mb-3">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                onClick={() => setActiveLang(lang)}
                className={`px-4 py-1 rounded text-sm border ${
                  activeLang === lang
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

            {[{ label: "Item Name", field: "title" },{ label:"shortDescription", field: "shortDescription" },{ label:"description", field: "description" }].map(({field, label }) => {
            const Component = field === "description" ? "textarea" : "input";
            label =
              label === "shortDescription"
                ? "Short Description"
                : label.charAt(0).toUpperCase() + label.slice(1);
            return (
              <div key={field} className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label} ({activeLang.toUpperCase()})
                </label>
                <Component
                  placeholder={`${label} (${activeLang})`}
                  value={translations[activeLang][field]}
                  onChange={(e) =>
                    setTranslations((prev) => ({
                      ...prev,
                      [activeLang]: {
                        ...prev[activeLang],
                        [field]: e.target.value,
                      },
                    }))
                  }
                  rows={field === "description" ? 4 : undefined}
                  className="border p-2 w-full rounded"
                />
              </div>
            );
          })}
        </div>

        {/* Category */}
        <label className="block mb-2 font-semibold">Subcategory</label>
        <div className="flex items-center gap-2 mb-4">
          <CategoryDropdown
            value={categoryId}
            onChange={(val) => setCategoryId(val)}
            categories={subcategories}
            placeholder="Select Subcategory"
          />

          {/* REFRESH BUTTON */}
          <button
            type="button"
            onClick={async () => { 
              if (refetchSubcategories) {
                setIsRefreshingCategories(true);
                try {
                  await refetchSubcategories();
                } finally {
                  setIsRefreshingCategories(false);
                }
              } 
            }}
            disabled={isRefreshingCategories}
            className="h-[42px] w-[42px] flex-shrink-0 bg-gray-100 hover:bg-gray-200 border rounded-lg text-gray-700 flex items-center justify-center disabled:opacity-50"
            title="Refresh Categories"
          >
            <FaSync className={isRefreshingCategories ? "animate-spin" : ""} />
          </button>

          {/* PLUS BUTTON */}
          <button
            type="button"
            onClick={() => setShowCategoryModal(true)}
            className="h-[42px] w-[42px] flex-shrink-0 bg-blue-600 hover:bg-blue-700 border rounded-lg text-white flex items-center justify-center"
            title="Add Category"
          >
            <FaPlus />
          </button>
        </div>

        {/* Images (shared across all variants) */}
        <ImageUploadPreview
          initialImages={imagesObj}
          onChange={(imgs) => setImages(imgs)} // This should be image names
          onChange2={(imgs) => setImagesObj(imgs)}
          onUploads={(files) => setUploads(files)}
        />
      </div>

      {/* ===== Variant Forms ===== */}
      {variantForms.map((id, index) => (
        <MenuVariantForm
          key={id}
          ref={(el) => (variantRefs.current[index] = el)}
          inventory={inventory}
          categoryId={categoryId}
          //   onDelete={() => removeVariantForm(index)}
          onDelete={index === 0 ? undefined : () => removeVariantForm(index)} // no delete for first
          index={index}
        />
      ))}

      <div className="flex gap-4 mt-4">
        <button
          onClick={addVariantForm}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          + Add Variant
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`bg-blue-600 text-white px-4 py-2 rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Add Category</h2>
            
            <div className="flex gap-2 mb-4">
              {supportedLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() =>
                    setNewCategory((prev) => ({ ...prev, activeLang: lang }))
                  }
                  className={`px-3 py-1 text-sm rounded-md border ${
                    newCategory.activeLang === lang
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Category Name ({newCategory.activeLang.toUpperCase()}) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newCategory.translations[newCategory.activeLang].name}
                onChange={(e) =>
                  setNewCategory((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [newCategory.activeLang]: {
                        ...prev.translations[newCategory.activeLang],
                        name: e.target.value,
                      },
                    },
                  }))
                }
                placeholder={`Enter category name (${newCategory.activeLang})`}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Description ({newCategory.activeLang.toUpperCase()}) <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <textarea
                rows={3}
                value={newCategory.translations[newCategory.activeLang].description}
                onChange={(e) =>
                  setNewCategory((prev) => ({
                    ...prev,
                    translations: {
                      ...prev.translations,
                      [newCategory.activeLang]: {
                        ...prev.translations[newCategory.activeLang],
                        description: e.target.value,
                      },
                    },
                  }))
                }
                placeholder={`Enter description (${newCategory.activeLang}) (optional)`}
                className="w-full border px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={categoryActionLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
              >
                {categoryActionLoading ? "Saving..." : "Add Category"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdvancedMenuForm;
